import {
  BADGE_STAT_KEYS,
  effectsForBadge,
  isBattleBadge,
  isTowerBadge,
  type BadgeStatKey,
  type BadgeStats,
} from 'features/catalog/badge-definitions';
import { DECK_LIMITS, RARITY_ORDER, type RarityName } from 'features/catalog/rarity-config';
import { nextUnit, shuffleInPlace } from 'features/battle/engine';
import { badgeRarity, badgeStatsFor } from 'features/battle/rules';
import { BATTLE_DECK_SIZE, type BattleOp } from 'features/battle/types';

export const BATTLE_DECK_CURVE: Record<RarityName, number> = {
  common: 5,
  rare: 4,
  legendary: 2,
  mythic: 1,
};

export type BattleDeckCard = {
  id: string;
  rarityName: RarityName;
  stats: BadgeStats;
};

const RECOMMEND_MIN_PRESTIGE = 3;
const SAMPLE_MIN_PRESTIGE = 5;
/** Deck of the Day: stats-only towers cannot exceed this. */
export const DECK_OF_THE_DAY_TOWERS = 2;

const rarityRank = (rarity: RarityName): number => RARITY_ORDER.indexOf(rarity);

export const badgeStatsTotal = (stats: BadgeStats): number =>
  BADGE_STAT_KEYS.reduce((sum, key) => sum + (stats[key] ?? 0), 0);

/** Higher total first; equal totals break toward the bigger peak (3/1 before 2/2). */
export const compareBadgeStatsOrder = (a: BadgeStats, b: BadgeStats): number => {
  const byTotal = badgeStatsTotal(b) - badgeStatsTotal(a);
  if (byTotal) return byTotal;
  const peakA = BADGE_STAT_KEYS.map((key) => a[key]).sort((left, right) => right - left);
  const peakB = BADGE_STAT_KEYS.map((key) => b[key]).sort((left, right) => right - left);
  for (let i = 0; i < peakA.length; i++) {
    const byPeak = peakB[i] - peakA[i];
    if (byPeak) return byPeak;
  }
  return 0;
};

const effectOnStat = (id: string, stat: BadgeStatKey): boolean =>
  effectsForBadge(id).some((effect) => effect.stat === stat);

/**
 * Catalog list under a stat dropdown: that stat's value, then whether the activate
 * effect is on it, then total stats. Unfiltered lists keep total / rarity / name.
 */
export const compareBattleCatalogOrder = (
  a: { id: string; rarityName: RarityName; stats: BadgeStats; name?: string },
  b: { id: string; rarityName: RarityName; stats: BadgeStats; name?: string },
  stat: BadgeStatKey | 'all'
): number => {
  if (stat !== 'all') {
    const byStat = b.stats[stat] - a.stats[stat];
    if (byStat) return byStat;
    const byEffect = Number(effectOnStat(b.id, stat)) - Number(effectOnStat(a.id, stat));
    if (byEffect) return byEffect;
  }
  return (
    compareBadgeStatsOrder(a.stats, b.stats) ||
    rarityRank(a.rarityName) - rarityRank(b.rarityName) ||
    (a.name ?? a.id).localeCompare(b.name ?? b.id)
  );
};

/** Mythic → Legendary → Rare → Common, then higher total / peak stats, then name. */
export const compareBattleDeckOrder = (
  a: { id: string; rarityName: RarityName; stats: BadgeStats; name?: string },
  b: { id: string; rarityName: RarityName; stats: BadgeStats; name?: string }
): number =>
  rarityRank(a.rarityName) - rarityRank(b.rarityName) ||
  compareBadgeStatsOrder(a.stats, b.stats) ||
  (a.name ?? a.id).localeCompare(b.name ?? b.id);

export const sortBattleDeckIds = (ids: readonly string[]): string[] =>
  [...ids].sort((left, right) =>
    compareBattleDeckOrder(
      { id: left, rarityName: badgeRarity(left), stats: badgeStatsFor(left) },
      { id: right, rarityName: badgeRarity(right), stats: badgeStatsFor(right) }
    )
  );

const countAtOrAbove = (rarities: readonly RarityName[], tier: RarityName): number =>
  rarities.filter((rarity) => rarityRank(rarity) <= rarityRank(tier)).length;

/** The deck limit (see `DECK_LIMITS`) that adding a `rarity` badge would break, or null if it fits. */
export const deckLimitBlocking = (rarities: readonly RarityName[], rarity: RarityName): RarityName | null =>
  RARITY_ORDER.find(
    (tier) => rarityRank(rarity) <= rarityRank(tier) && countAtOrAbove(rarities, tier) >= DECK_LIMITS[tier]
  ) ?? null;

/** The deck limit this deck already exceeds, or null if it is within all of them. */
export const deckLimitExceeded = (rarities: readonly RarityName[]): RarityName | null =>
  RARITY_ORDER.find((tier) => countAtOrAbove(rarities, tier) > DECK_LIMITS[tier]) ?? null;

/** Walk a ranked or shuffled pool and take any card that still fits `DECK_LIMITS`. */
const takeLegalDeck = (
  cards: readonly BattleDeckCard[],
  size: number,
  already: readonly RarityName[] = [],
  maxTowers = Number.POSITIVE_INFINITY
): string[] => {
  const used = new Set<string>();
  const out: string[] = [];
  const rarities: RarityName[] = [...already];
  let towers = 0;
  for (const card of cards) {
    if (out.length >= size) break;
    if (used.has(card.id)) continue;
    if (isTowerBadge(card.id) && towers >= maxTowers) continue;
    if (deckLimitBlocking(rarities, card.rarityName)) continue;
    used.add(card.id);
    out.push(card.id);
    rarities.push(card.rarityName);
    if (isTowerBadge(card.id)) towers += 1;
  }
  return out;
};

const grantsSelfPrestige = (ops: readonly BattleOp[]): boolean =>
  ops.some((op) => {
    if (op.kind === 'gain_prestige') return op.who !== 'opponent';
    if (op.kind === 'steal_prestige' || op.kind === 'gain_prestige_per_drawn') return true;
    if (op.kind === 'neighbor_activate_prestige') return true;
    if (op.kind === 'if') return grantsSelfPrestige(op.then) || grantsSelfPrestige(op.otherwise ?? []);
    return false;
  });

export const badgeCanGainPrestige = (id: string): boolean =>
  effectsForBadge(id).some((effect) => grantsSelfPrestige(effect.ops));

const effectStat = (id: string): BadgeStatKey | null => effectsForBadge(id)[0]?.stat ?? null;

const RECOMMEND_FOCUS_MIN = 2;
const RECOMMEND_FOCUS_MAX = 3;

const rollRecommendFocus = (rng: number): { focus: BadgeStatKey[]; rng: number } => {
  const countPick = pickIndex(RECOMMEND_FOCUS_MAX - RECOMMEND_FOCUS_MIN + 1, rng);
  const shuffled = shuffleInPlace([...BADGE_STAT_KEYS], countPick.rng);
  return { focus: shuffled.items.slice(0, RECOMMEND_FOCUS_MIN + countPick.index), rng: shuffled.rng };
};

/** The 2 or 3 effect stats a Randomize Deck seed builds around. */
export const recommendDeckFocus = (seed: number): BadgeStatKey[] => rollRecommendFocus(seed || 1).focus;

/** On-theme (and stat-less) cards first, shuffled; off-theme shuffled behind them. Rarity is not ranked. */
const focusedThenRest = (
  cards: readonly BattleDeckCard[],
  focus: readonly BadgeStatKey[],
  rng: number
): { items: BattleDeckCard[]; rng: number } => {
  const onTheme: BattleDeckCard[] = [];
  const offTheme: BattleDeckCard[] = [];
  cards.forEach((card) => {
    const stat = effectStat(card.id);
    if (stat == null || focus.includes(stat)) onTheme.push(card);
    else offTheme.push(card);
  });
  const shuffledOn = shuffleInPlace(onTheme, rng);
  const shuffledOff = shuffleInPlace(offTheme, shuffledOn.rng);
  return { items: [...shuffledOn.items, ...shuffledOff.items], rng: shuffledOff.rng };
};

const pickIndex = (length: number, rng: number): { index: number; rng: number } => {
  const roll = nextUnit(rng);
  return { index: Math.floor(roll.value * length), rng: roll.rng };
};

const ensurePrestigeCards = (
  deck: string[],
  byId: Map<string, BattleDeckCard>,
  unused: BattleDeckCard[],
  min: number,
  startRng: number,
  locked: ReadonlySet<string> = new Set(),
  maxTowers = Number.POSITIVE_INFINITY
): string[] => {
  const selected = [...deck];
  const inDeck = new Set(selected);
  let rng = startRng;
  const prestigeCount = () => selected.filter((id) => badgeCanGainPrestige(id)).length;
  const target = Math.min(min, unused.length + prestigeCount());
  const pool = [...unused];
  while (prestigeCount() < target && pool.length) {
    const incoming = pool.shift();
    if (!incoming || inDeck.has(incoming.id)) continue;
    const sameRarity = selected
      .map((id, index) => ({ id, index, card: byId.get(id) }))
      .filter(
        ({ id, card }) => !locked.has(id) && card?.rarityName === incoming.rarityName && !badgeCanGainPrestige(id)
      );
    const replaceable = sameRarity.length
      ? sameRarity
      : selected
          .map((id, index) => ({ id, index, card: byId.get(id) }))
          .filter(({ id, index }) => {
            if (locked.has(id) || badgeCanGainPrestige(id)) return false;
            const others = selected.filter((_, i) => i !== index).map(badgeRarity);
            if (isTowerBadge(incoming.id) && !isTowerBadge(id) && selected.filter(isTowerBadge).length >= maxTowers) {
              return false;
            }
            return !deckLimitBlocking(others, incoming.rarityName);
          });
    if (!replaceable.length) break;
    const pick = pickIndex(replaceable.length, rng);
    rng = pick.rng;
    const slot = replaceable[pick.index];
    inDeck.delete(slot.id);
    selected[slot.index] = incoming.id;
    inDeck.add(incoming.id);
  }
  return selected;
};

/**
 * Starter 12 from owned battle cards. Picks 2 or 3 effect stats, shuffles those
 * cards, and takes any mix that still sits inside `DECK_LIMITS` — no rarity curve.
 * Stat-less badges can still land. Prestige-gain cards are kept when the pool has them.
 * `keep` stays put; only the open slots are rolled, and Prestige swaps will not evict them.
 */
export const recommendBattleDeck = (
  owned: readonly BattleDeckCard[],
  size = BATTLE_DECK_SIZE,
  seed?: number,
  keep: readonly string[] = []
): string[] => {
  const eligible = owned.filter((card) => isBattleBadge(card.id));
  const byId = new Map(eligible.map((card) => [card.id, card]));
  const kept: string[] = [];
  const keptSet = new Set<string>();
  const keptRarities: RarityName[] = [];
  for (const id of keep) {
    if (kept.length >= size || keptSet.has(id) || !isBattleBadge(id)) continue;
    kept.push(id);
    keptSet.add(id);
    keptRarities.push(byId.get(id)?.rarityName ?? badgeRarity(id));
  }
  let rng = (seed ?? Math.floor(Math.random() * 0x7fffffff)) || 1;
  const focusRoll = rollRecommendFocus(rng);
  rng = focusRoll.rng;
  const rest = eligible.filter((card) => !keptSet.has(card.id));
  const ordered = focusedThenRest(rest, focusRoll.focus, rng);
  const deck = [...kept, ...takeLegalDeck(ordered.items, size - kept.length, keptRarities)];
  const unusedPrestige = eligible.filter((card) => badgeCanGainPrestige(card.id) && !deck.includes(card.id));
  const prestigePool = shuffleInPlace(unusedPrestige, ordered.rng);
  return ensurePrestigeCards(deck, byId, prestigePool.items, RECOMMEND_MIN_PRESTIGE, prestigePool.rng, keptSet);
};

/**
 * Random 12 from a shuffled battle pool. Any mix is fine as long as it fits `DECK_LIMITS`.
 * Five Prestige-gain cards are kept when the pool has them.
 */
export const sampleBattleDeck = (ids: readonly string[], size = BATTLE_DECK_SIZE, seed = 1): string[] => {
  const cards: BattleDeckCard[] = [];
  ids.forEach((id) => {
    if (!isBattleBadge(id)) return;
    cards.push({ id, rarityName: badgeRarity(id), stats: badgeStatsFor(id) });
  });
  const shuffled = shuffleInPlace(cards, seed || 1);
  const deck = takeLegalDeck(shuffled.items, size);
  const byId = new Map(cards.map((card) => [card.id, card]));
  const unusedPrestige = cards.filter((card) => badgeCanGainPrestige(card.id) && !deck.includes(card.id));
  const prestigePool = shuffleInPlace(unusedPrestige, shuffled.rng);
  return ensurePrestigeCards(deck, byId, prestigePool.items, SAMPLE_MIN_PRESTIGE, prestigePool.rng);
};

/** Same builder the CPU uses, seeded so one calendar day keeps a single 12. */
export const deckOfTheDaySeed = (date = new Date()): number => {
  const day = date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate();
  return day || 1;
};

/** The 2 or 3 effect stats a date's Deck of the Day builds around. */
export const deckOfTheDayFocus = (date = new Date()): BadgeStatKey[] =>
  rollRecommendFocus(deckOfTheDaySeed(date)).focus;

/**
 * Seeded by the date, so everyone gets the same 12. Picks two or three effect stats,
 * shuffles those cards, and takes any mix that still sits inside `DECK_LIMITS`,
 * with at most 2 towers.
 */
export const deckOfTheDayIds = (ids: readonly string[], date = new Date()): string[] => {
  const focus = deckOfTheDayFocus(date);
  const cards: BattleDeckCard[] = ids
    .filter((id) => isBattleBadge(id))
    .map((id) => ({ id, rarityName: badgeRarity(id), stats: badgeStatsFor(id) }));
  const ordered = focusedThenRest(cards, focus, deckOfTheDaySeed(date) * 7 + 1);
  const deck = takeLegalDeck(ordered.items, BATTLE_DECK_SIZE, [], DECK_OF_THE_DAY_TOWERS);
  const byId = new Map(cards.map((card) => [card.id, card]));
  const unusedPrestige = cards.filter((card) => badgeCanGainPrestige(card.id) && !deck.includes(card.id));
  const prestigePool = shuffleInPlace(unusedPrestige, ordered.rng);
  return ensurePrestigeCards(
    deck,
    byId,
    prestigePool.items,
    SAMPLE_MIN_PRESTIGE,
    prestigePool.rng,
    new Set(),
    DECK_OF_THE_DAY_TOWERS
  );
};

export const deckCurveNote = (cards: readonly BattleDeckCard[]): string | null => {
  if (cards.length < BATTLE_DECK_SIZE) {
    const need = BATTLE_DECK_SIZE - cards.length;
    return `Add ${need} more badge${need === 1 ? '' : 's'} to battle (${cards.length}/${BATTLE_DECK_SIZE}).`;
  }
  const over = deckLimitExceeded(cards.map((card) => card.rarityName));
  if (over && DECK_LIMITS[over] < BATTLE_DECK_SIZE) {
    return `Too many ${over}+ badges — max ${DECK_LIMITS[over]} per deck.`;
  }
  const prestige = cards.filter((card) => badgeCanGainPrestige(card.id)).length;
  if (prestige === 0) {
    return 'No Prestige effects — this deck cannot score.';
  }
  if (prestige < RECOMMEND_MIN_PRESTIGE) {
    return `Only ${prestige} Prestige card${prestige === 1 ? '' : 's'} — add more that can score.`;
  }
  return null;
};

const rarityCounts = (deck: readonly BattleDeckCard[]): Record<RarityName, number> => {
  const counts: Record<RarityName, number> = { mythic: 0, legendary: 0, rare: 0, common: 0 };
  deck.forEach((card) => {
    counts[card.rarityName] += 1;
  });
  return counts;
};

/**
 * How well this badge belongs with the deck.
 * Shared effect stats still matter, and so does the 5/4/2/1 curve: a rarity the deck
 * is short on ranks up, a rarity it already has too many of ranks down. Prestige
 * sources rank up while the deck has fewer than two. `focus` is the stat filter
 * the player is browsing, so cards that actually activate on that stat come first.
 */
export const deckSynergyScore = (
  badge: BattleDeckCard,
  deck: readonly BattleDeckCard[],
  focus?: BadgeStatKey
): number => {
  if (!deck.length) return 0;
  const deckEffectStats = new Set(
    deck.map((card) => effectStat(card.id)).filter((stat): stat is BadgeStatKey => Boolean(stat))
  );
  const stat = effectStat(badge.id);
  let score = stat && deckEffectStats.has(stat) ? 8 : 0;
  if (focus && stat === focus) score += 6;
  BADGE_STAT_KEYS.forEach((key) => {
    if (badge.stats[key] <= 0) return;
    if (deck.some((card) => card.stats[key] > 0)) score += badge.stats[key];
  });
  const gap = BATTLE_DECK_CURVE[badge.rarityName] - rarityCounts(deck)[badge.rarityName];
  score += Math.max(-2, Math.min(2, gap)) * 4;
  const prestigeInDeck = deck.filter((card) => badgeCanGainPrestige(card.id)).length;
  if (prestigeInDeck < 2 && badgeCanGainPrestige(badge.id)) score += 5;
  return score;
};
